import createPrivateAdminPanelInstance from "../api/PrivateAdminPanelInstance";
import { createCasinoPresignedUrl } from "../queries/casinoFm/useCasinoPresignedUrl";

type PresignedData = {
  upload_url?: string;
  s3_key?: string;
  s3_bucket?: string;
  cloudfront_url?: string;
  content_type?: string;
  expires_in?: number;
  http_method?: string;
};

type PresignedResponse = PresignedData & {
  data?: PresignedData;
  success?: boolean;
  message?: string;
};

type CasinoImageUploadParams = {
  imageType: string;
  identifier: string | number;
  contentType: string;
  file: Blob;
  provider?: string;
  sub?: string;
  existingUrl?: string;
};

export const buildS3UrlFromPresigned = (
  presigned?: PresignedResponse | null,
): string | null => {
  const data = presigned?.data || presigned || {};
  if (data?.cloudfront_url) {
    return data.cloudfront_url;
  }
  if (data?.s3_bucket && data?.s3_key) {
    return `https://${data.s3_bucket}.s3.amazonaws.com/${data.s3_key}`;
  }
  return null;
};

export const uploadCasinoImageToS3 = async ({
  imageType,
  identifier,
  contentType,
  file,
  provider,
  sub,
  existingUrl,
}: CasinoImageUploadParams): Promise<PresignedResponse> => {
  if (!imageType || !identifier || !contentType || !file) {
    throw new Error("Missing required data for image upload");
  }

  const presigned = (await createCasinoPresignedUrl({
    image_type: imageType,
    identifier: String(identifier),
    content_type: contentType,
    ...(provider ? { provider } : {}),
    ...(sub ? { sub } : {}),
    existing_url: existingUrl || null,
  })) as PresignedResponse | null;

  const uploadUrl = presigned?.data?.upload_url || presigned?.upload_url;
  const httpMethod =
    presigned?.data?.http_method || presigned?.http_method || "PUT";

  if (!uploadUrl) {
    throw new Error("Failed to generate pre-signed URL");
  }

  const s3Instance = createPrivateAdminPanelInstance();
  const response = await s3Instance.request({
    url: uploadUrl,
    method: httpMethod,
    headers: contentType ? { "Content-Type": contentType } : undefined,
    data: file,
  });

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`S3 upload failed with status ${response.status}`);
  }

  return presigned || {};
};
